import DescriptionIcon from '@mui/icons-material/Description';
import ForumIcon from '@mui/icons-material/Forum';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import {
    Box,
    Chip,
    Paper,
    Typography,
    useTheme
} from '@mui/material';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import { useAppContext } from '../../context/AppContext';
import { LessonPlanSlide as LessonPlanSlideType } from '../../object-actions/types/types';
import CoachingTip from './CoachingTip';
import ScriptsDisplay from './ScriptsDisplay';

interface LessonPlanSlideProps {
    slide: LessonPlanSlideType;
    slideNumber: number;
    totalSlides: number;
}

const LessonPlanSlide: React.FC<LessonPlanSlideProps> = ({ slide, slideNumber, totalSlides }) => {
    const theme = useTheme();
    const { membership } = useAppContext();
    const availableRoles = membership?.roles || [];

    const slideTypeConfig = {
        activity: {
            label: 'Activity',
            color: 'primary' as const,
            icon: <TrackChangesIcon sx={{ fontSize: '1.5rem' }} />,
            bgColor: theme.palette.mode === 'dark'
                ? 'rgba(25, 118, 210, 0.1)'
                : 'rgba(25, 118, 210, 0.08)',
            borderColor: theme.palette.primary.main
        },
        worksheet: {
            label: 'Worksheet',
            color: 'secondary' as const,
            icon: <DescriptionIcon sx={{ fontSize: '1.5rem' }} />,
            bgColor: theme.palette.mode === 'dark'
                ? 'rgba(156, 39, 176, 0.1)'
                : 'rgba(156, 39, 176, 0.08)',
            borderColor: theme.palette.secondary.main
        },
        conversation: {
            label: 'Conversation',
            color: 'success' as const,
            icon: <ForumIcon sx={{ fontSize: '1.5rem' }} />,
            bgColor: theme.palette.mode === 'dark'
                ? 'rgba(46, 125, 50, 0.1)'
                : 'rgba(46, 125, 50, 0.08)',
            borderColor: theme.palette.success.main
        }
    };

    const config = slideTypeConfig[slide.type];

    return (
        <Paper
            elevation={2}
            sx={{
                p: 3,
                mb: 3,
                backgroundColor: config.bgColor,
                borderLeft: `4px solid ${config.borderColor}`,
                borderRadius: 2,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                }
            }}
        >
            {/* Slide Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        {config.icon}
                        <Typography
                            variant="h5"
                            component="h3"
                            sx={{
                                fontWeight: 'bold',
                                color: 'text.primary',
                                flex: 1, flexGrow: 1
                            }}
                        >
                            {slide.title} <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 1 }}
                            >
                                {slideNumber} of {totalSlides}
                            </Typography>
                        </Typography>
                        <Chip
                            label={config.label}
                            color={config.color}
                            size="small"
                            sx={{ fontWeight: 600 }}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Slide Body */}
            {slide.body && (
                <Box
                    sx={{
                        mt: 2,
                        mb: 2,
                        '& p': { margin: 0 },
                        '& p + p': { marginTop: 2 },
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                            marginTop: 2,
                            marginBottom: 1,
                            fontWeight: 'bold',
                            color: 'text.primary'
                        },
                        '& ul, & ol': {
                            marginTop: 1,
                            marginBottom: 1,
                            paddingLeft: 3
                        },
                        '& li': {
                            marginBottom: 0.5
                        },
                        '& a': {
                            color: theme.palette.primary.main,
                            textDecoration: 'none',
                            '&:hover': {
                                textDecoration: 'underline'
                            }
                        },
                        '& blockquote': {
                            borderLeft: `4px solid ${theme.palette.divider}`,
                            paddingLeft: 2,
                            marginLeft: 0,
                            fontStyle: 'italic',
                            color: 'text.secondary',
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.05)'
                                : 'rgba(0, 0, 0, 0.02)',
                            padding: 1.5,
                            borderRadius: 1,
                            marginTop: 2,
                            marginBottom: 2
                        },
                        '& code': {
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(0, 0, 0, 0.05)',
                            padding: '2px 6px',
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.9em'
                        },
                        '& pre': {
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.05)'
                                : 'rgba(0, 0, 0, 0.02)',
                            padding: 2,
                            borderRadius: 1,
                            overflow: 'auto',
                            marginTop: 2,
                            marginBottom: 2
                        },
                        color: 'text.primary',
                        lineHeight: 1.7
                    }}
                >
                    <ReactMarkdown>{slide.body}</ReactMarkdown>
                </Box>
            )}

            {/* Coaching Tip */}
            {slide.coaching_tip && availableRoles.includes('coach') && (
                <CoachingTip tip={slide.coaching_tip} />
            )}

            {/* Scripts Section */}
            <ScriptsDisplay scripts={slide.scripts} />

        </Paper>
    );
};

export default LessonPlanSlide;

